import * as React from 'react'
import * as Sentry from '@sentry/react'
import {
  usePaymentsActivateTrialMutation,
  useSubscriptionOptimisticallySetStateMutation,
} from '../../gen/stripe'
import { userQueryFetcher } from 'auth/hooks'
import { isLoggedInQuerySelector } from 'auth/selectors'
import { PaymentStatus, SubscriptionType, TrialActivationReason } from '../../gen/types'
import { SentryFilterTagOneClickUpgradeButton } from '@repo/sentry-utils/constants'
import { trackYearlySubscriptionStarted } from '@repo/analytics/google-site-tag'
import type { UpgradeSource } from '../../upgrade-sources'
import { getActivateTrialPaymentResult } from '../../lib/activate-trial/get-activate-trial-payment-result'
import { getSubscriptionToActivate } from './get-subscription-to-activate'

interface FailingReason {
  type: 'noUser' | 'noAllAccess' | 'noStudioAi' | 'noSubscriptionId' | 'upgradeFailed' | 'unknown'
  message: string
}

export interface ActivateTrialReturn {
  type: 'upgrade'
  paymentsStatus?: PaymentStatus
  message?: string | null
  userBanned?: boolean | null
  error: FailingReason | null
}

interface ActivateTrialProps {
  upgradeSource: UpgradeSource
}

interface ActivateTrialSubscription {
  isProcessing: boolean
  activateTrial: (props: ActivateTrialProps) => Promise<ActivateTrialReturn> | ActivateTrialReturn
}

function mapSubscriptionVariantToSubscriptionType(
  subscriptionType: 'all-access' | 'studio-ai',
): SubscriptionType {
  if (subscriptionType === 'all-access') {
    return SubscriptionType.AllAccessYearlyPromo
  }

  return SubscriptionType.StudioAiYearly
}

export function useActivateTrial({
  subscriptionType = 'all-access',
  shouldApplyBonus = false,
}: {
  subscriptionType: 'all-access' | 'studio-ai'
  shouldApplyBonus?: boolean
}): ActivateTrialSubscription {
  const [isProcessing, setIsProcessing] = React.useState(false)

  const activateTrialMutation = usePaymentsActivateTrialMutation({
    retry: 3,
    retryDelay: attempt => attempt * 1000,
  })
  const optimisticallyUpdateSubscriptionStateMutation =
    useSubscriptionOptimisticallySetStateMutation()

  const handleActivateTrial = React.useCallback(
    async ({ upgradeSource }: ActivateTrialProps): Promise<ActivateTrialReturn> => {
      setIsProcessing(true)
      const user = await userQueryFetcher()
      const isUserLoggedIn = isLoggedInQuerySelector(user)
      const userData = user?.me?.user

      const userHasAllAccess = userData?.subscriptionAccess.allAccess.isSubscribed
      const isEligibleForAllAccessFreeTrial =
        userData?.subscriptionAccess.allAccess.isEligibleForFreeTrial

      const userHasStudioAi = userData?.subscriptionAccess.studioAi.isSubscribed
      const isEligibleForStudioAiFreeTrial =
        userData?.subscriptionAccess.studioAi.isEligibleForFreeTrial

      const subscriptionToUpgrade = getSubscriptionToActivate({
        subscriptionType,
        userData,
      })

      if (!isUserLoggedIn || !userData) {
        setIsProcessing(false)
        return {
          type: 'upgrade',
          error: { type: 'noUser', message: '' },
          paymentsStatus: undefined,
          message: null,
        }
      }

      if (
        subscriptionType === 'all-access' &&
        (isEligibleForAllAccessFreeTrial || !userHasAllAccess)
      ) {
        setIsProcessing(false)
        return {
          type: 'upgrade',
          error: { type: 'noAllAccess', message: '' },
          paymentsStatus: undefined,
          message: null,
        }
      }

      if (
        subscriptionType === 'studio-ai' &&
        (isEligibleForStudioAiFreeTrial || !userHasStudioAi)
      ) {
        setIsProcessing(false)
        return {
          type: 'upgrade',
          error: { type: 'noStudioAi', message: '' },
          paymentsStatus: undefined,
          message: null,
        }
      }

      if (!subscriptionToUpgrade?.isTrial) {
        setIsProcessing(false)
        return {
          type: 'upgrade',
          error: { type: 'upgradeFailed', message: '' },
          paymentsStatus: undefined,
          message: null,
        }
      }

      let stripeSubscriptionIdToUpgrade = subscriptionToUpgrade?.id ?? null

      try {
        if (stripeSubscriptionIdToUpgrade === null || stripeSubscriptionIdToUpgrade.length === 0) {
          setIsProcessing(false)
          return {
            type: 'upgrade',
            error: { type: 'noSubscriptionId', message: '' },
            paymentsStatus: undefined,
            message: null,
          }
        }

        const activateTrialResponse = await activateTrialMutation.mutateAsync({
          input: {
            id: stripeSubscriptionIdToUpgrade,
            activationReason: TrialActivationReason.UserRequest,
            activationSource: upgradeSource,
          },
          applyBonus: shouldApplyBonus,
        })

        const activateTrialData = activateTrialResponse.paymentsActivateTrial
        const activateTrialResult = getActivateTrialPaymentResult(activateTrialData)

        if (!activateTrialData?.subscription && !activateTrialData?.errors?.length) {
          Sentry.captureException(`Failed to activate yearly subscription: No data returned`, {
            tags: { component: SentryFilterTagOneClickUpgradeButton },
          })
          return {
            type: 'upgrade',
            error: { type: 'upgradeFailed', message: '' },
            paymentsStatus: undefined,
            message: null,
          }
        }

        if (activateTrialResult.paymentStatus === PaymentStatus.Succeeded) {
          trackYearlySubscriptionStarted()
        }

        if (activateTrialResult.paymentStatus === PaymentStatus.Succeeded) {
          const nextSubscriptionType = mapSubscriptionVariantToSubscriptionType(subscriptionType)
          const optimisticallyUpdateResponse =
            await optimisticallyUpdateSubscriptionStateMutation.mutateAsync({
              input: {
                userId: userData.id,
                subscription: nextSubscriptionType,
              },
            })

          const errors = optimisticallyUpdateResponse?.subscriptionOptimisticallySetState?.errors

          if (errors && errors.length > 0) {
            Sentry.captureException(
              new Error(
                `Optimistically updating subscription state failed: ${errors.map(e => e.message).join(', ')}`,
              ),
              {
                tags: { component: SentryFilterTagOneClickUpgradeButton },
              },
            )
          }
        }

        return {
          type: 'upgrade',
          error: null,
          paymentsStatus: activateTrialResult.paymentStatus,
          userBanned: null,
          message: activateTrialResult.hostedUrl,
        }
      } catch (error) {
        Sentry.captureException(
          new Error(
            `Failed to activate yearly subscription - unknown error: ${new Error(
              (error as Error)?.message || (error as string),
            )}`,
          ),
          {
            tags: { component: SentryFilterTagOneClickUpgradeButton },
          },
        )
        return {
          type: 'upgrade',
          error: {
            type: 'unknown',
            message: (error as Error)?.message || (error as string),
          },
          paymentsStatus: undefined,
          message: null,
        }
      } finally {
        setIsProcessing(false)
      }
    },
    [
      activateTrialMutation,
      optimisticallyUpdateSubscriptionStateMutation,
      shouldApplyBonus,
      subscriptionType,
    ],
  )

  return {
    isProcessing,
    activateTrial: handleActivateTrial,
  }
}
